import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, MapPin, MessageSquareWarning, Repeat, CalendarDays, CheckCircle2, ShoppingBag, IndianRupee, ExternalLink, Users, Check, User as UserIcon, Mail, Phone, Home, ListChecks, Clock, CalendarX, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ChefRatingWidget from "@/components/ChefRatingWidget";

const DAYS = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
const MEALS = ["breakfast","lunch","dinner"];
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const STORES = [
  { id: "blinkit",   name: "Blinkit",   color: "bg-yellow-100 text-yellow-900 border-yellow-200",   url: (q) => `https://blinkit.com/s/?q=${encodeURIComponent(q)}` },
  { id: "zepto",     name: "Zepto",     color: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-200", url: (q) => `https://www.zeptonow.com/search?query=${encodeURIComponent(q)}` },
  { id: "bigbasket", name: "BigBasket", color: "bg-emerald-100 text-emerald-900 border-emerald-200", url: (q) => `https://www.bigbasket.com/ps/?q=${encodeURIComponent(q)}` },
  { id: "amazon",    name: "Amazon Fresh", color: "bg-orange-100 text-orange-900 border-orange-200", url: (q) => `https://www.amazon.in/s?k=${encodeURIComponent(q)}&i=nowstore` },
];
const groupBy = (arr, key) => arr.reduce((m, it) => { (m[it[key] || "Other"] ||= []).push(it); return m; }, {});

const LEAD_STATUS_LABEL = {
  new: "Request Submitted",
  request_submitted: "Request Submitted",
  trial_scheduled: "Trial Scheduled",
  payment_pending: "Payment Pending",
  payment_done: "Payment Complete",
  payment_complete: "Payment Complete",
  rejected: "Rejected / Cancelled",
  pushed_to_customer: "Action Needed",
  contacted: "Contacted",
  trial_done: "Trial Done",
  converted: "Active",
};
const LEAD_STATUS_TONE = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  request_submitted: "bg-blue-100 text-blue-800 border-blue-200",
  trial_scheduled: "bg-purple-100 text-purple-800 border-purple-200",
  payment_pending: "bg-orange-100 text-orange-800 border-orange-200",
  payment_done: "bg-emerald-100 text-emerald-800 border-emerald-200",
  payment_complete: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
  pushed_to_customer: "bg-indigo-100 text-indigo-800 border-indigo-200",
  contacted: "bg-sky-100 text-sky-800 border-sky-200",
  trial_done: "bg-cyan-100 text-cyan-800 border-cyan-200",
  converted: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

// Profile dummy data shown when the user hasn't filled in their own profile yet.
// Demo customer has real seeded profile values from wipe_db.py.
const DUMMY_PROFILE = {
  address: "B-204, Sapphire Heights, Sector 75",
  city: "Noida, UP 201301",
  household_size: 4,
  dietary: "veg",
  allergies: ["peanut"],
  cuisines: ["North Indian", "South Indian"],
  preferred_slot: "07:30 – 09:00",
};


function PlanCard({ p, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(p.id)}
      className={`text-left card-default cursor-pointer transition-all hover:-translate-y-0.5 ${selected ? "border-primary ring-2 ring-primary/20" : ""}`}
      data-testid={`select-plan-${p.id}`}
    >
      <div className="flex items-start justify-between">
        <h3 className="heading text-xl font-medium">{p.name}</h3>
        {selected && <Check size={18} strokeWidth={2.5} className="text-primary mt-1"/>}
      </div>
      <p className="text-sm text-muted-foreground mt-1">{p.tagline}</p>
      <div className="text-xs uppercase tracking-[0.2em] font-bold mt-4 text-muted-foreground">{p.meals}</div>
    </button>
  );
}

export default function CustomerDashboard() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [menu, setMenu] = useState(() => {
    const m = {}; DAYS.forEach(d => { m[d] = { breakfast: "", lunch: "", dinner: "" }; });
    return m;
  });
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); const day = d.getDay() || 7;
    if (day !== 1) d.setHours(-24*(day-1));
    return d.toISOString().slice(0,10);
  });
  const [complaints, setComplaints] = useState([]);
  const [slotReqs, setSlotReqs] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [chefLoc, setChefLoc] = useState(null);
  const [currentSlot, setCurrentSlot] = useState(null);
  const [myLeads, setMyLeads] = useState([]);
  const [myPayments, setMyPayments] = useState([]);
  const [myChef, setMyChef] = useState(null);
  const [chefLeaves, setChefLeaves] = useState([]);
  const [pickingFor, setPickingFor] = useState(null); // lead being scheduled
  const [pickDate, setPickDate] = useState("");
  const [pickTime, setPickTime] = useState("");
  const [showPayQr, setShowPayQr] = useState(null); // payment object
  const [tab, setTab] = useState(params.get("tab") || "overview");
  const [busy, setBusy] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [customRange, setCustomRange] = useState(() => {
    const t = new Date();
    const e = new Date(); e.setDate(e.getDate() + 6);
    return { start: t.toISOString().slice(0,10), end: e.toISOString().slice(0,10) };
  });

  const load = useCallback(async () => {
    try {
      const [s, p, cs, sr, cl, cur, m, ccr, ml, mp, mc] = await Promise.all([
        api.get("/customer/subscription"),
        api.get("/plans"),
        api.get("/customer/complaints"),
        api.get("/customer/slot-requests"),
        api.get("/customer/chef-location"),
        api.get("/customer/current-slot"),
        api.get("/customer/menu"),
        api.get("/customer/chef-change-requests"),
        api.get("/customer/leads"),
        api.get("/customer/payments"),
        api.get("/customer/my-chef"),
      ]);
      setSub(s.data && Object.keys(s.data).length ? s.data : null);
      setPlans(p.data);
      setComplaints(cs.data);
      setSlotReqs(sr.data);
      setChefLoc(cl.data && Object.keys(cl.data).length ? cl.data : null);
      setCurrentSlot(cur.data && Object.keys(cur.data).length ? cur.data : null);
      setChefChangeReqs(ccr.data);
      setMyLeads(ml.data || []);
      setMyPayments(mp.data || []);
      setMyChef(mc.data && Object.keys(mc.data).length ? mc.data : null);
      // Fetch chef leaves once we know who the chef is
      if (mc.data?.chef?.id) {
        try {
          const lv = await api.get(`/customer/chef-leaves?chef_id=${mc.data.chef.id}`);
          setChefLeaves(lv.data || []);
        } catch { setChefLeaves([]); }
      } else {
        setChefLeaves([]);
      }
      if (m.data?.[0]) {
        setMenu(prev => ({ ...prev, ...m.data[0].meals }));
        setWeekStart(m.data[0].week_start);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/customer/chef-location");
        if (data && Object.keys(data).length) setChefLoc(data);
      } catch { /* ignore polling errors */ }
    }, 30000);
    return () => clearInterval(t);
  }, []);

  const computePayable = useCallback((planId) => {
    const p = plans.find(x => x.id === planId);
    if (!p) return 0;
    if (planId !== "custom") return p.price;
    const sd = new Date(customRange.start);
    const ed = new Date(customRange.end);
    const days = Math.floor((ed - sd) / 86400000) + 1;
    return Math.max(days, 0) * (p.price || 200);
  }, [plans, customRange]);

  const proceedToPay = async () => {
    if (!selectedPlan) return;
    setBusy(true);
    try {
      const payload = { plan: selectedPlan };
      if (selectedPlan === "custom") {
        if (customRange.end < customRange.start) {
          toast.error("End date must be on or after start date"); setBusy(false); return;
        }
        payload.start_date = customRange.start; payload.end_date = customRange.end;
      }
      const { data } = await api.post("/payments/create-order", payload);
      if (data.mock || !data.key_id) {
        await api.post("/payments/verify", { order_id: data.order_id });
        toast.success("Subscription activated (demo mode). Add Razorpay keys to enable live checkout.");
        await load(); setSelectedPlan(null); setTab("overview");
      } else {
        await new Promise((res, rej) => {
          if (window.Razorpay) return res();
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = res; s.onerror = rej;
          document.body.appendChild(s);
        });
        const rz = new window.Razorpay({
          key: data.key_id, amount: data.amount, currency: data.currency,
          order_id: data.order_id, name: "Delco Chefs", description: `${selectedPlan} plan`,
          handler: async (r) => {
            try {
              await api.post("/payments/verify", {
                order_id: data.order_id,
                razorpay_payment_id: r.razorpay_payment_id,
                razorpay_signature: r.razorpay_signature,
              });
              toast.success("Payment successful!");
              await load(); setSelectedPlan(null); setTab("overview");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          },
          theme: { color: "#E27D60" },
        });
        rz.open();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const saveMenu = async () => {
    try {
      await api.post("/customer/menu", { week_start: weekStart, meals: menu });
      toast.success("Menu saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const genForecast = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/customer/forecast", {});
      setForecast(data);
      toast.success("Forecast ready");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const fileComplaint = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.post("/customer/complaints", { category: fd.get("category"), message: fd.get("message") });
      toast.success("Complaint filed");
      e.target.reset();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [slotForm, setSlotForm] = useState({
    sH: "07", sM: "00", eH: "08", eM: "00",
    reason: "",
  });
  const [chefChangeReason, setChefChangeReason] = useState("");
  const [chefChangeReqs, setChefChangeReqs] = useState([]);

  // Unavailability dates: load and manage on Support tab.
  const [unavailability, setUnavailability] = useState([]);
  const [unavailForm, setUnavailForm] = useState({
    mode: "range",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    reason: "",
  });
  const loadUnavailability = useCallback(async () => {
    try {
      const { data } = await api.get("/customer/unavailability");
      setUnavailability(data || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadUnavailability(); }, [loadUnavailability]);

  const addUnavailability = async (e) => {
    e.preventDefault();
    const payload = {
      start_date: unavailForm.start_date,
      end_date: unavailForm.mode === "single" ? unavailForm.start_date : unavailForm.end_date,
      reason: unavailForm.reason,
    };
    if (payload.end_date < payload.start_date) { toast.error("End date must be on or after start"); return; }
    try {
      await api.post("/customer/unavailability", payload);
      toast.success("Marked unavailable");
      setUnavailForm({ mode: unavailForm.mode, start_date: payload.start_date, end_date: payload.end_date, reason: "" });
      loadUnavailability();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };
  const removeUnavailability = async (uid) => {
    if (!window.confirm("Remove this unavailability window?")) return;
    try {
      await api.delete(`/customer/unavailability/${uid}`);
      toast.success("Removed");
      loadUnavailability();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't remove"); }
  };

  const requestSlot = async (e) => {
    e.preventDefault();
    const new_slot = `${slotForm.sH}:${slotForm.sM}-${slotForm.eH}:${slotForm.eM}`;
    if (new_slot.split("-")[0] >= new_slot.split("-")[1]) {
      toast.error("End time must be after start time"); return;
    }
    try {
      await api.post("/customer/slot-change", {
        new_slot,
        reason: slotForm.reason,
      });
      toast.success("Slot change request submitted");
      setSlotForm({ sH: "07", sM: "00", eH: "08", eM: "00", reason: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const requestChefChange = async (e) => {
    e.preventDefault();
    if (!chefChangeReason.trim()) { toast.error("Please share a reason"); return; }
    try {
      await api.post("/customer/chef-change", { reason: chefChangeReason });
      toast.success("Chef change request submitted");
      setChefChangeReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmTrialSlot = async () => {
    if (!pickingFor || !pickDate || !pickTime) { toast.error("Pick a date and time"); return; }
    try {
      await api.post(`/customer/leads/${pickingFor.id}/pick-trial-slot`, { date: pickDate, time: pickTime });
      toast.success("Trial scheduled");
      setPickingFor(null); setPickDate(""); setPickTime("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't confirm"); }
  };

  const submitTrialDecision = async (leadId, action) => {
    const label = action === "start_subscription" ? "subscribe now" : "cancel this request";
    if (!window.confirm(`Are you sure you want to ${label}?`)) return;
    try {
      await api.post(`/customer/leads/${leadId}/trial-decision`, { action });
      toast.success(
        action === "start_subscription" ? "Great! We'll send your payment link shortly." : "Request cancelled."
      );
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't submit"); }
  };

  const cancelSubscription = async (leadId) => {
    if (!window.confirm("Cancel this subscription? Future payment requests will stop. You can submit a fresh request later.")) return;
    try {
      await api.post(`/customer/leads/${leadId}/cancel-subscription`);
      toast.success("Subscription cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't cancel"); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="heading text-3xl font-bold">Your kitchen</h1>
            <p className="text-muted-foreground">Plan meals, track your chef, and forecast rations.</p>
          </div>
          {sub ? (
            <Badge className="bg-secondary text-secondary-foreground rounded-full px-4 py-2" data-testid="sub-badge">
              <CheckCircle2 size={14} className="mr-1"/> {sub.plan} · active
            </Badge>
          ) : null}
        </div>

        <Tabs value={tab} onValueChange={setTab} data-testid="customer-tabs">
          <TabsList className="bg-muted rounded-full p-1 flex-wrap h-auto">
            <TabsTrigger value="overview" className="rounded-full" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="requests" className="rounded-full" data-testid="tab-requests">My Requests</TabsTrigger>
            <TabsTrigger value="profile" className="rounded-full" data-testid="tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="chef" className="rounded-full" data-testid="tab-chef">My Chef</TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-full" data-testid="tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="menu" className="rounded-full" data-testid="tab-menu">Menu</TabsTrigger>
            <TabsTrigger value="forecast" className="rounded-full" data-testid="tab-forecast">Forecast</TabsTrigger>
            <TabsTrigger value="support" className="rounded-full" data-testid="tab-support">Support</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="card-default">
              <div className="flex items-center gap-2 mb-3"><MapPin size={18} className="text-primary"/><h3 className="heading text-lg font-medium">Your chef · Live</h3></div>
              {(() => {
                // Tracking is enabled only within 15 minutes before the slot or while the slot is active.
                const mappingSlot = chefLoc?.mapping?.slot; // e.g. "07:30"
                let trackingOpen = !!currentSlot; // if a slot is in progress, allow
                if (!trackingOpen && mappingSlot) {
                  const [hh, mm] = mappingSlot.split(":").map(Number);
                  const t = new Date(); t.setHours(hh, mm, 0, 0);
                  const minsAway = (t - Date.now()) / 60000;
                  // Allow tracking from 15 minutes before until 4 hours after the slot start
                  trackingOpen = minsAway <= 15 && minsAway >= -240;
                }
                const mapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
                const srcAt = (lat, lng) => mapsKey
                  ? `https://www.google.com/maps/embed/v1/place?key=${mapsKey}&q=${lat},${lng}&zoom=15`
                  : `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
                const srcGeneric = mapsKey
                  ? `https://www.google.com/maps/embed/v1/place?key=${mapsKey}&q=Noida`
                  : "https://maps.google.com/maps?q=Noida&z=12&output=embed";
                if (!chefLoc) return <p className="text-sm text-muted-foreground">No chef mapped yet. Admin will assign one shortly.</p>;
                const todayStr = new Date().toISOString().slice(0, 10);
                const sd = chefLoc.mapping?.start_date;
                const ed = chefLoc.mapping?.end_date;
                const periodLabel = sd && ed
                  ? (sd === ed ? sd : `${sd} → ${ed}`)
                  : todayStr;
                return (
                  <div>
                    <div className="text-sm"><b>{chefLoc.chef?.name}</b> · {chefLoc.mapping?.service_type}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <CalendarDays size={12}/> <span>{periodLabel}</span>
                      <span>·</span>
                      <Clock size={12}/> <span>slot {chefLoc.mapping?.slot}</span>
                    </div>
                    {!trackingOpen ? (
                      <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="tracking-gate">
                        🔒 Live tracking opens <b>15 minutes before</b> your slot. Until then, chef location is not shared.
                      </div>
                    ) : chefLoc.location ? (
                      <div className="mt-3 text-xs text-muted-foreground">
                        Last seen: {new Date(chefLoc.location.updated_at).toLocaleTimeString()}<br/>
                        Lat {chefLoc.location.lat.toFixed(4)}, Lng {chefLoc.location.lng.toFixed(4)}
                      </div>
                    ) : <div className="text-xs text-muted-foreground mt-2">Chef hasn&apos;t shared location yet.</div>}
                    {trackingOpen && (
                      <>
                        <iframe
                          className="mt-4 w-full h-56 rounded-xl border border-border"
                          title="chef-map"
                          loading="lazy"
                          src={chefLoc.location ? srcAt(chefLoc.location.lat, chefLoc.location.lng) : srcGeneric}
                          data-testid="chef-map-iframe"
                        />
                        {chefLoc.location && (
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${chefLoc.location.lat},${chefLoc.location.lng}`}
                            target="_blank" rel="noreferrer"
                            className="inline-block mt-2 text-xs text-primary underline"
                            data-testid="open-navigate"
                          >Open in Google Maps →</a>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="card-default">
              <div className="flex items-center gap-2 mb-3"><CalendarDays size={18} className="text-primary"/><h3 className="heading text-lg font-medium">Today&rsquo;s slot</h3></div>
              {currentSlot ? (
                <div className="space-y-2 text-sm">
                  <div>Status: <Badge className="rounded-full">{currentSlot.status}</Badge></div>
                  <div>Started at: {new Date(currentSlot.started_at).toLocaleTimeString()}</div>
                </div>
              ) : <p className="text-sm text-muted-foreground">No active slot right now.</p>}
            </div>
          </TabsContent>

          <TabsContent value="requests" className="mt-6">
            <div className="card-default">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <ListChecks size={18} className="text-primary"/>
                  <h3 className="heading text-xl font-medium">My requests</h3>
                  <Badge variant="outline" className="rounded-full">{myLeads.length}</Badge>
                </div>
                <Button variant="outline" className="rounded-full" onClick={()=>window.location.href="/onboarding"} data-testid="new-request-btn">+ New request</Button>
              </div>
              {myLeads.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10">
                  You haven&apos;t submitted any requests yet. Tap <b>+ New request</b> to get started.
                </p>
              ) : (
                <div className="space-y-3">
                  {myLeads.map(l => {
                    const status = l.status || "new";
                    // Admin opened trial picker, customer hasn't picked yet
                    const needsToPickSlot = status === "trial_scheduled" && !l.trial_confirmed_slot;
                    // Trial scheduled and the trial datetime has passed → show 3 actions
                    let trialPassed = false;
                    if (l.trial_confirmed_slot && !l.trial_decision) {
                      const dt = new Date(`${l.trial_confirmed_slot.date}T${l.trial_confirmed_slot.time}:00`);
                      trialPassed = dt.getTime() <= Date.now();
                    }
                    const pendingPay = status === "payment_pending";
                    const lastPay = myPayments.find(p => p.lead_id === l.id);
                    return (
                      <div key={l.id} className="p-4 rounded-xl border border-border bg-card" data-testid={`my-lead-${l.id}`}>
                        <div className="flex items-start justify-between flex-wrap gap-2">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h4 className="heading text-base font-semibold capitalize">{l.service_type} service</h4>
                              <Badge className={`rounded-full text-[10px] border ${LEAD_STATUS_TONE[status] || ""}`}>{LEAD_STATUS_LABEL[status] || status}</Badge>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Submitted {new Date(l.created_at).toLocaleDateString()} · {l.total_persons} pax · {l.dietary}
                            </div>
                            {l.trial_confirmed_slot && (
                              <div className="mt-2 inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 rounded-full px-2.5 py-0.5 text-[11px]">
                                <Clock size={11}/> Trial: {l.trial_confirmed_slot.date} at {l.trial_confirmed_slot.time}
                              </div>
                            )}
                          </div>
                        </div>

                        {needsToPickSlot && (
                          <div className="mt-3 p-3 rounded-lg bg-purple-50 border border-purple-200" data-testid={`trial-pick-${l.id}`}>
                            <div className="text-sm font-semibold text-purple-900 mb-2">Schedule your trial</div>
                            {l.trial_admin_note && <p className="text-xs text-purple-800 mb-2 italic">&ldquo;{l.trial_admin_note}&rdquo;</p>}
                            <p className="text-xs text-purple-800 mb-2">Pick any date and time between <b>6 AM – 9 PM</b>.</p>
                            <Button
                              size="sm"
                              className="rounded-full bg-purple-600 hover:bg-purple-700 text-white"
                              onClick={()=>{ setPickingFor(l); setPickDate(new Date().toISOString().slice(0,10)); setPickTime("07:00"); }}
                              data-testid={`open-trial-picker-${l.id}`}
                            >
                              <Clock size={12} className="mr-1"/>Pick date &amp; time
                            </Button>
                          </div>
                        )}

                        {trialPassed && (
                          <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid={`trial-decision-${l.id}`}>
                            <div className="text-sm font-semibold text-amber-900 mb-2">How was your trial?</div>
                            <p className="text-xs text-amber-800 mb-3">Choose what you&apos;d like to do next.</p>
                            <div className="flex gap-2 flex-wrap">
                              <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={()=>submitTrialDecision(l.id, "start_subscription")}
                                data-testid={`decision-start-${l.id}`}>
                                <CheckCircle2 size={12} className="mr-1"/>Subscribe
                              </Button>
                              <Button size="sm" variant="outline" className="rounded-full text-rose-600 border-rose-300 hover:bg-rose-50"
                                onClick={()=>submitTrialDecision(l.id, "cancel")}
                                data-testid={`decision-cancel-${l.id}`}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}

                        {pendingPay && lastPay && (
                          <div className="mt-3 p-3 rounded-lg bg-orange-50 border border-orange-200" data-testid={`pay-block-${l.id}`}>
                            <div className="text-sm font-semibold text-orange-900 mb-1">Payment pending · ₹{Number(lastPay.amount).toLocaleString("en-IN")}</div>
                            <p className="text-xs text-orange-800 mb-2">{lastPay.description}</p>
                            {lastPay.qr_image_url && (
                              <div className="flex gap-3 items-center flex-wrap mt-2 mb-3">
                                <img src={lastPay.qr_image_url} alt="UPI QR" width={140} height={140} className="rounded-lg border border-orange-200 bg-white p-1"/>
                                <div className="text-xs text-orange-900 space-y-1">
                                  <div className="font-semibold">Scan with any UPI app</div>
                                  <div>GPay · PhonePe · Paytm</div>
                                  <div className="text-[10px] text-orange-700">Amount is pre-filled</div>
                                </div>
                              </div>
                            )}
                            <div className="flex gap-2 flex-wrap">
                              <Button size="sm" className="rounded-full bg-orange-600 hover:bg-orange-700 text-white" onClick={()=>setShowPayQr(lastPay)} data-testid={`pay-now-${l.id}`}>
                                <IndianRupee size={12} className="mr-1"/>View payment
                              </Button>
                              {lastPay.upi_uri && (
                                <a href={lastPay.upi_uri}>
                                  <Button size="sm" variant="outline" className="rounded-full">Open in UPI app</Button>
                                </a>
                              )}
                            </div>
                          </div>
                        )}

                        {(status === "payment_done" || status === "payment_complete") && (
                          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                            <div className="text-xs text-emerald-700 inline-flex items-center gap-1">
                              <CheckCircle2 size={12}/> Payment confirmed. Your subscription is active.
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full text-rose-600 border-rose-300 hover:bg-rose-50 text-xs"
                              onClick={()=>cancelSubscription(l.id)}
                              data-testid={`cancel-sub-${l.id}`}
                            >
                              Cancel subscription
                            </Button>
                          </div>
                        )}

                        {status === "rejected" && (
                          <div className="mt-3 text-xs text-rose-700">
                            This request was cancelled. To try again, submit a fresh request.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            {(() => {
              const profile = user?.profile || {};
              const display = {
                address: profile.address || DUMMY_PROFILE.address,
                city: profile.city || DUMMY_PROFILE.city,
                household_size: profile.household_size || DUMMY_PROFILE.household_size,
                dietary: profile.dietary || DUMMY_PROFILE.dietary,
                allergies: profile.allergies?.length ? profile.allergies : DUMMY_PROFILE.allergies,
                cuisines: profile.cuisines?.length ? profile.cuisines : DUMMY_PROFILE.cuisines,
                preferred_slot: profile.preferred_slot || DUMMY_PROFILE.preferred_slot,
              };
              return (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="card-default">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                        <UserIcon size={26} className="text-primary"/>
                      </div>
                      <div>
                        <h3 className="heading text-xl font-semibold">{user?.name || "—"}</h3>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">{user?.role || "customer"}</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground"><Phone size={14}/> {user?.phone || "—"}</div>
                      <div className="flex items-center gap-2 text-muted-foreground"><Mail size={14}/> {user?.email || "—"}</div>
                      <div className="flex items-start gap-2 text-muted-foreground"><Home size={14} className="mt-0.5"/> <span>{display.address}<br/>{display.city}</span></div>
                      <div className="flex items-center gap-2 text-muted-foreground"><Users size={14}/> Household of {display.household_size}</div>
                      <div className="flex items-center gap-2 text-muted-foreground"><Clock size={14}/> Preferred slot: {display.preferred_slot}</div>
                    </div>
                  </div>

                  <div className="card-default">
                    <h3 className="heading text-lg font-semibold mb-4">Preferences</h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Diet</div>
                        <Badge variant="outline" className="rounded-full capitalize">{display.dietary}</Badge>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Allergies</div>
                        <div className="flex gap-1.5 flex-wrap">
                          {display.allergies.length === 0
                            ? <span className="text-muted-foreground text-xs">None</span>
                            : display.allergies.map(a => <Badge key={a} className="rounded-full bg-rose-100 text-rose-800 border-rose-200">{a}</Badge>)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Favourite cuisines</div>
                        <div className="flex gap-1.5 flex-wrap">
                          {display.cuisines.map(c => <Badge key={c} variant="outline" className="rounded-full">{c}</Badge>)}
                        </div>
                      </div>
                    </div>
                    {!profile.address && (
                      <p className="text-[11px] text-muted-foreground mt-4 italic">
                        Showing sample data — your details will appear here once our team finalises your trial.
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="chef" className="mt-6">
            {myChef?.chef ? (
              <div className="card-default" data-testid="my-chef-card">
                <div className="flex items-start gap-4 mb-5 flex-wrap">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <UserIcon size={28} className="text-primary"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="heading text-2xl font-semibold">{myChef.chef.name}</h3>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">Your assigned chef</p>
                    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                      {myChef.chef.phone && <div className="flex items-center gap-2"><Phone size={13}/> {myChef.chef.phone}</div>}
                      {myChef.chef.email && <div className="flex items-center gap-2"><Mail size={13}/> {myChef.chef.email}</div>}
                    </div>
                  </div>
                </div>
                {myChef.chef.profile && (myChef.chef.profile.experience_years || myChef.chef.profile.specialties?.length > 0) && (
                  <div className="border-t border-border pt-4 mb-4">
                    <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Profile</h4>
                    {myChef.chef.profile.experience_years && (
                      <div className="text-sm">Experience: <b>{myChef.chef.profile.experience_years} years</b></div>
                    )}
                    {myChef.chef.profile.specialties?.length > 0 && (
                      <div className="text-sm mt-1">Specialties: {myChef.chef.profile.specialties.join(", ")}</div>
                    )}
                  </div>
                )}
                <div className="border-t border-border pt-4">
                  <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Documents</h4>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {Object.entries(myChef.chef.documents_available || {})
                      .filter(([k]) => k === "photo" || k === "bgv")
                      .map(([k, available]) => (
                      <div key={k} className={`p-3 rounded-lg border flex items-center justify-between ${available ? "border-emerald-200 bg-emerald-50" : "border-border bg-muted/30"}`} data-testid={`chef-doc-${k}`}>
                        <div>
                          <div className="text-sm font-medium capitalize">
                            {k === "bgv" ? "Background Verification (BGV)" : "Profile photo"}
                          </div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">
                            {available ? "Verified" : "Pending"}
                          </div>
                        </div>
                        {available && (
                          <a
                            href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/customer/chef-document/${k}`}
                            target="_blank" rel="noreferrer"
                            className="text-xs text-primary underline"
                          >View</a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="card-default text-center py-12 text-muted-foreground">
                <UserIcon size={36} className="mx-auto mb-3 opacity-40"/>
                <p className="text-sm">No chef has been assigned yet. You&apos;ll see your chef&apos;s profile here after your subscription begins.</p>
              </div>
            )}
            {myChef?.chef && <ChefRatingWidget />}
          </TabsContent>

          <TabsContent value="calendar" className="mt-6">
            <div className="card-default" data-testid="overview-calendar">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                  <h3 className="heading text-xl font-medium">Service calendar</h3>
                  <p className="text-sm text-muted-foreground">Upcoming chef leaves and service-off days.</p>
                </div>
              </div>
              {chefLeaves.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No upcoming chef leaves.</p>
              ) : (
                <div className="space-y-2">
                  {chefLeaves.map(lv => (
                    <div key={lv.id} className="flex items-center justify-between p-3 rounded-lg border border-amber-200 bg-amber-50" data-testid={`leave-${lv.id}`}>
                      <div>
                        <div className="font-medium text-sm">Chef leave</div>
                        <div className="text-xs text-muted-foreground">{lv.start_date} → {lv.end_date}</div>
                        {lv.reason && <div className="text-xs italic mt-0.5">&ldquo;{lv.reason}&rdquo;</div>}
                      </div>
                      <Badge className="bg-amber-200 text-amber-900 border-amber-300 rounded-full text-[10px]">Service off</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="menu" className="mt-6">
            <div className="card-default">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="heading text-xl font-medium">Week of {weekStart}</h3>
                  <p className="text-sm text-muted-foreground">Plan each meal for the week.</p>
                </div>
                <div className="flex gap-2">
                  <Input type="date" value={weekStart} onChange={e=>setWeekStart(e.target.value)} className="w-44" data-testid="menu-week" />
                  <Button onClick={saveMenu} className="rounded-full bg-primary hover:bg-primary/90" data-testid="menu-save">Save menu</Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-muted-foreground"><th className="py-2">Day</th>{MEALS.map(m=><th key={m} className="py-2 capitalize">{m}</th>)}</tr></thead>
                  <tbody>
                    {DAYS.map(d => (
                      <tr key={d} className="border-t border-border">
                        <td className="py-2 capitalize font-medium">{d}</td>
                        {MEALS.map(m => (
                          <td key={m} className="py-2 pr-2">
                            <Input value={menu[d][m]} onChange={e=>setMenu({...menu, [d]: {...menu[d], [m]: e.target.value}})}
                              placeholder="e.g., Poha" data-testid={`menu-${d}-${m}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="forecast" className="mt-6">
            <div className="card-default">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div className="flex items-center gap-2"><Sparkles size={18} className="text-primary"/><h3 className="heading text-xl font-medium">AI ration forecast</h3></div>
                <Button onClick={genForecast} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90" data-testid="forecast-gen">
                  {busy ? "Generating..." : forecast ? "Regenerate" : "Generate forecast"}
                </Button>
              </div>
              {forecast ? (() => {
                const items = forecast.data.items || [];
                const grouped = groupBy(items, "category");
                return (
                  <div className="space-y-6">
                    {/* Cost banner */}
                    <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">Estimated weekly cost</div>
                        <div className="heading text-4xl font-bold mt-1 flex items-center"><IndianRupee size={26} strokeWidth={2.5}/>{forecast.data.estimated_cost_inr ?? 0}</div>
                      </div>
                      <div className="text-xs text-muted-foreground max-w-sm text-right">
                        {items.length} items · grouped by category · tap a store to order
                      </div>
                    </div>

                    {/* Category sections */}
                    {Object.entries(grouped).map(([cat, list]) => (
                      <div key={cat}>
                        <div className="flex items-center gap-2 mb-3">
                          <ShoppingBag size={14} className="text-muted-foreground"/>
                          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{cat}</h4>
                          <div className="flex-1 h-px bg-border"/>
                          <span className="text-xs text-muted-foreground">{list.length} items</span>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {list.map((it, i) => (
                            <div key={i} className="p-4 rounded-2xl bg-card border border-border hover:border-primary/40 hover:shadow-[0_8px_24px_rgba(44,42,41,0.06)] transition-all" data-testid={`forecast-item-${cat}-${i}`}>
                              <div className="flex items-baseline justify-between gap-3">
                                <h5 className="heading font-semibold leading-tight">{it.name}</h5>
                                <div className="text-right shrink-0">
                                  <div className="heading text-lg font-bold text-primary leading-none">{it.quantity}</div>
                                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{it.unit}</div>
                                </div>
                              </div>
                              <div className="mt-3 pt-3 border-t border-border">
                                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Order from</div>
                                <div className="flex flex-wrap gap-1.5">
                                  {STORES.map(s => (
                                    <a key={s.id} href={s.url(it.name)} target="_blank" rel="noreferrer"
                                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border ${s.color} hover:opacity-80 transition-opacity`}
                                      data-testid={`buy-${s.id}-${i}`}>
                                      {s.name} <ExternalLink size={10}/>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    {forecast.data.notes && (
                      <div className="rounded-xl bg-muted border border-border p-3 text-xs text-muted-foreground">
                        <b>AI note:</b> {forecast.data.notes}
                      </div>
                    )}
                  </div>
                );
              })() : (
                <div className="py-12 text-center">
                  <div className="w-14 h-14 rounded-full bg-muted mx-auto mb-3 flex items-center justify-center"><Sparkles size={22} className="text-muted-foreground"/></div>
                  <p className="text-sm text-muted-foreground">Save a menu first, then tap <b>Generate forecast</b> for a weekly shopping list with one-tap ordering links.</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="support" className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            <div className="card-default xl:col-span-3" data-testid="unavailability-card">
              <div className="flex items-center gap-2 mb-3">
                <CalendarX size={18} className="text-primary"/>
                <h3 className="heading text-lg font-medium">Mark unavailable days</h3>
                <Badge variant="outline" className="rounded-full text-[10px]">Synced with admin</Badge>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Going on a trip or skipping service? Add the dates here and admin will pause your chef visits.
              </p>

              <form onSubmit={addUnavailability} className="grid md:grid-cols-5 gap-3 items-end" data-testid="unavail-form">
                <div className="md:col-span-1">
                  <Label className="text-xs">Type</Label>
                  <Select value={unavailForm.mode} onValueChange={(v)=>setUnavailForm({...unavailForm, mode: v})}>
                    <SelectTrigger data-testid="unavail-mode"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single day</SelectItem>
                      <SelectItem value="range">Date range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{unavailForm.mode === "single" ? "Date" : "Start"}</Label>
                  <Input
                    type="date"
                    required
                    value={unavailForm.start_date}
                    min={new Date().toISOString().slice(0,10)}
                    onChange={(e)=>setUnavailForm({...unavailForm, start_date: e.target.value, end_date: e.target.value > unavailForm.end_date ? e.target.value : unavailForm.end_date})}
                    data-testid="unavail-start"
                  />
                </div>
                {unavailForm.mode === "range" && (
                  <div>
                    <Label className="text-xs">End</Label>
                    <Input
                      type="date"
                      required
                      value={unavailForm.end_date}
                      min={unavailForm.start_date}
                      onChange={(e)=>setUnavailForm({...unavailForm, end_date: e.target.value})}
                      data-testid="unavail-end"
                    />
                  </div>
                )}
                <div className={unavailForm.mode === "range" ? "md:col-span-1" : "md:col-span-2"}>
                  <Label className="text-xs">Reason (optional)</Label>
                  <Input
                    value={unavailForm.reason}
                    onChange={(e)=>setUnavailForm({...unavailForm, reason: e.target.value})}
                    placeholder="e.g. Travelling, festival"
                    data-testid="unavail-reason"
                  />
                </div>
                <div>
                  <Button type="submit" className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="unavail-submit">Add</Button>
                </div>
              </form>

              <div className="mt-5">
                {unavailability.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No upcoming unavailability windows.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {unavailability.map(u => (
                      <div key={u.id} className="p-3 rounded-xl border border-amber-200 bg-amber-50 flex items-start justify-between gap-2" data-testid={`unavail-${u.id}`}>
                        <div>
                          <div className="text-sm font-medium">
                            {u.start_date === u.end_date ? u.start_date : `${u.start_date} → ${u.end_date}`}
                          </div>
                          {u.reason && <div className="text-xs text-muted-foreground italic mt-0.5">{u.reason}</div>}
                        </div>
                        <button
                          onClick={()=>removeUnavailability(u.id)}
                          className="text-rose-600 hover:bg-rose-100 rounded p-1"
                          aria-label="Remove"
                          data-testid={`unavail-remove-${u.id}`}
                        >
                          <Trash2 size={14}/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="card-default">
              <div className="flex items-center gap-2 mb-3"><MessageSquareWarning size={18} className="text-primary"/><h3 className="heading text-lg font-medium">Raise a complaint</h3></div>
              <form onSubmit={fileComplaint} className="space-y-3" data-testid="complaint-form">
                <div>
                  <Label>Category</Label>
                  <Select name="category" defaultValue="service">
                    <SelectTrigger data-testid="complaint-category"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="chef">Chef</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                      <SelectItem value="food">Food</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>What happened?</Label>
                  <Textarea name="message" required data-testid="complaint-msg" />
                </div>
                <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="complaint-submit">Submit</Button>
              </form>
              <div className="mt-5 space-y-2">
                {complaints.map(c => (
                  <div key={c.id} className="p-3 rounded-xl bg-muted border border-border text-sm">
                    <div className="flex justify-between"><b className="capitalize">{c.category}</b><Badge variant="outline" className="rounded-full">{c.status}</Badge></div>
                    <div className="text-muted-foreground mt-1">{c.message}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-default">
              <div className="flex items-center gap-2 mb-3"><Repeat size={18} className="text-primary"/><h3 className="heading text-lg font-medium">Request slot change</h3></div>
              <form onSubmit={requestSlot} className="space-y-4" data-testid="slot-req-form">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">Preferred new slot</Label>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 bg-muted rounded-xl p-1">
                      <Select value={slotForm.sH} onValueChange={v=>setSlotForm({...slotForm, sH:v})}>
                        <SelectTrigger className="h-9 w-16 border-0 bg-transparent" data-testid="slot-sh"><SelectValue/></SelectTrigger>
                        <SelectContent className="max-h-64">{HOURS.map(h=><SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                      </Select>
                      <span className="font-bold text-muted-foreground">:</span>
                      <Select value={slotForm.sM} onValueChange={v=>setSlotForm({...slotForm, sM:v})}>
                        <SelectTrigger className="h-9 w-16 border-0 bg-transparent" data-testid="slot-sm"><SelectValue/></SelectTrigger>
                        <SelectContent className="max-h-64">{MINS.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <span className="text-muted-foreground">→</span>
                    <div className="flex items-center gap-1 bg-muted rounded-xl p-1">
                      <Select value={slotForm.eH} onValueChange={v=>setSlotForm({...slotForm, eH:v})}>
                        <SelectTrigger className="h-9 w-16 border-0 bg-transparent" data-testid="slot-eh"><SelectValue/></SelectTrigger>
                        <SelectContent className="max-h-64">{HOURS.map(h=><SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                      </Select>
                      <span className="font-bold text-muted-foreground">:</span>
                      <Select value={slotForm.eM} onValueChange={v=>setSlotForm({...slotForm, eM:v})}>
                        <SelectTrigger className="h-9 w-16 border-0 bg-transparent" data-testid="slot-em"><SelectValue/></SelectTrigger>
                        <SelectContent className="max-h-64">{MINS.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">Pick any hour and minute combination · 24-hour format</div>
                </div>
                <div>
                  <Label>Reason</Label>
                  <Textarea value={slotForm.reason} onChange={e=>setSlotForm({...slotForm, reason:e.target.value})} required data-testid="slot-reason" />
                </div>
                <Button className="rounded-full bg-secondary hover:bg-secondary/90" data-testid="slot-submit">Submit slot change</Button>
              </form>
              <div className="mt-5 space-y-2">
                {slotReqs.map(r => (
                  <div key={r.id} className="p-3 rounded-xl bg-muted border border-border text-sm">
                    <div className="flex justify-between"><b>{r.new_slot}</b><Badge variant="outline" className="rounded-full">{r.status}</Badge></div>
                    <div className="text-muted-foreground mt-1">{r.reason}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-default">
              <div className="flex items-center gap-2 mb-3"><Users size={18} className="text-primary"/><h3 className="heading text-lg font-medium">Request chef change</h3></div>
              <form onSubmit={requestChefChange} className="space-y-3" data-testid="chef-req-form">
                <div>
                  <Label>Why would you like a different chef?</Label>
                  <Textarea value={chefChangeReason} onChange={e=>setChefChangeReason(e.target.value)} required data-testid="chef-reason" placeholder="Punctuality, taste, communication..."/>
                </div>
                <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="chef-submit">Submit chef change</Button>
              </form>
              <div className="mt-5 space-y-2">
                {chefChangeReqs.map(r => (
                  <div key={r.id} className="p-3 rounded-xl bg-muted border border-border text-sm">
                    <div className="flex justify-between"><b>Chef change</b><Badge variant="outline" className="rounded-full">{r.status}</Badge></div>
                    <div className="text-muted-foreground mt-1">{r.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={!!pickingFor} onOpenChange={(o) => { if (!o) { setPickingFor(null); setPickDate(""); setPickTime(""); } }}>
          <DialogContent data-testid="confirm-trial-dialog">
            <DialogHeader>
              <DialogTitle>Schedule your trial</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Pick any date and time (<b>6 AM – 9 PM</b>) for your <b className="capitalize">{pickingFor?.service_type}</b> trial.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    min={new Date().toISOString().slice(0,10)}
                    value={pickDate}
                    onChange={e=>setPickDate(e.target.value)}
                    data-testid="trial-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Time</Label>
                  <Select value={pickTime} onValueChange={setPickTime}>
                    <SelectTrigger data-testid="trial-time-input"><SelectValue placeholder="Pick a time"/></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {(() => {
                        const opts = [];
                        for (let h = 6; h <= 21; h++) {
                          for (const m of [0, 30]) {
                            if (h === 21 && m === 30) continue;
                            const t = `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
                            opts.push(<SelectItem key={t} value={t}>{t}</SelectItem>);
                          }
                        }
                        return opts;
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={()=>{ setPickingFor(null); setPickDate(""); setPickTime(""); }}>Cancel</Button>
                <Button className="bg-primary hover:bg-primary/90" onClick={confirmTrialSlot} data-testid="confirm-trial-btn">Confirm</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!showPayQr} onOpenChange={(o) => { if (!o) setShowPayQr(null); }}>
          <DialogContent data-testid="pay-qr-dialog" className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Scan to pay</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-center">
              {showPayQr?.qr_image_url && (
                <img src={showPayQr.qr_image_url} alt="UPI QR" className="mx-auto rounded-xl border border-border bg-white p-2" width={260} height={260}/>
              )}
              <div className="text-3xl heading font-bold inline-flex items-center justify-center">
                <IndianRupee size={24} strokeWidth={2.5}/>{Number(showPayQr?.amount || 0).toLocaleString("en-IN")}
              </div>
              <p className="text-xs text-muted-foreground">{showPayQr?.description}</p>
              {showPayQr?.upi_uri && (
                <a href={showPayQr.upi_uri}>
                  <Button className="rounded-full bg-primary hover:bg-primary/90 w-full">Open in UPI app</Button>
                </a>
              )}
              <Button variant="outline" className="rounded-full w-full" onClick={()=>setShowPayQr(null)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
